'use client'

import React, { useState, useEffect } from 'react'
import Button from '@/components/ui/Button'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import { useCookieConsent } from '@/context/cookie-consent-context/CookieConsentContext'
import { CookieConsent } from '@/types/cookie-consent'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog"
import Image from 'next/image'

const COOKIE_CATEGORIES = [
  {
    id: 'essential',
    name: 'Essential',
    description: 'These cookies are necessary for the website to function and cannot be switched off in our systems.',
    // icon: Shield,
    required: true,
    // examples: 'Authentication, security, language preferences'
  },
  {
    id: 'analytics',
    name: 'Analytics',
    description: 'Help us understand how visitors interact with the website by collecting and reporting information anonymously.',
    // icon: BarChart3,
    required: false,
    // examples: 'Google Analytics, page views, user behavior'
  },
  {
    id: 'marketing',
    name: 'Marketing',
    description: 'Used to track visitors across websites to deliver more relevant ads and content based on your interests.',
    // icon: Target,
    required: false,
    // examples: 'Ad targeting, social media integration, remarketing'
  },
  // {
  //   id: 'preferences',
  //   name: 'Preference Cookies',
  //   description: 'These cookies remember your choices and preferences to provide a more personalized experience.',
  //   icon: SettingsIcon,
  //   required: false,
  //   examples: 'Theme settings, layout preferences, saved filters'
  // }
] as const

export function CookiePreferencesModal() {
  const { 
    showPreferences, 
    closePreferences, 
    savePreferences, 
    acceptAll, 
    rejectAll,
    consent 
  } = useCookieConsent()

  const [preferences, setPreferences] = useState<Partial<CookieConsent>>({
    essential: true,
    analytics: false,
    marketing: false,
    // preferences: false
  })

  useEffect(() => {
    if (consent) {
      setPreferences({
        essential: consent.essential,
        analytics: consent.analytics,
        marketing: consent.marketing,
        // preferences: consent.preferences
      })
    }
  }, [consent])

  const handleToggle = (category: string, enabled: boolean) => {
    setPreferences(prev => ({
      ...prev,
      [category]: enabled
    }))
  }

  const handleSave = () => {
    savePreferences(preferences)
  }

  const handleAcceptAll = () => {
    setPreferences({
      essential: true,
      analytics: true,
      marketing: true,
      // preferences: true
    })
    acceptAll()
  }

  const handleRejectAll = () => {
    setPreferences({
      essential: true,
      analytics: false,
      marketing: false,
      // preferences: false
    })
    rejectAll()
  }

  // if (!showPreferences) return null
  return (
    <Dialog open={showPreferences} onOpenChange={closePreferences}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-hidden gap-0" hideCloseButton>
        <DialogHeader className="pt-0 pb-2">
          <div className="flex items-center justify-between gap-2">
            <DialogTitle className='text-title-text text-gray-900'>Customize Your Cookie Preferences</DialogTitle>
            <Button
              variant="close"
              size="close"
              onClick={closePreferences}
              className="flex-shrink-0"
              aria-label="Close dialog"
            >
              <Image
                src="/images/closeIcon.svg"
                alt="Close"
                width={24}
                height={24}
                className="w-6 h-6"
              />
            </Button>
          </div>
        </DialogHeader>
        
        <div className="max-h-[60vh] overflow-y-auto py-6">
          <p className="text-regular-normal text-gray-500 mb-4">
            We use cookies to enhance your experience, analyze site usage, and assist in our marketing efforts. You can manage your preferences below. Essential cookies are always active.
          </p>

          <div className="space-y-4">
            {COOKIE_CATEGORIES.map((category) => {
              // const IconComponent = category.icon
              const enabled = Boolean(preferences[category.id as keyof typeof preferences])
              
              return (
                  <div key={category.id}>
                    {/* Badge and Switch on same line */}
                    <div className="flex items-center justify-between mb-2">
                      <Badge variant="gray">{category.name}</Badge>
                      {!category.required && (
                        <Switch
                          checked={enabled}
                          onCheckedChange={(checked) => handleToggle(category.id, checked)}
                        />
                      )}
                    </div>
                    {/* Description text full width below */}
                    <p className="text-regular-normal text-gray-500">{category.description}</p>
                  </div>
                )
            })}
          </div>
        </div>

        <DialogFooter className="flex justify-end gap-3 pt-4 pb-0">
          <Button 
            onClick={closePreferences}
            variant="ghost"
            size="outline"
          >
            Cancel
          </Button>
          
          <Button 
            onClick={handleSave}
            variant="orange"
            size="outline"
          >
            Save Settings
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
} 